import {
  Component,
  linkedSignal,
  input,
  output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideSearch,
  lucideZap,
  lucideCheck,
  lucideChevronDown,
  lucidePlus,
  lucideX,
} from '@ng-icons/lucide';
import { BrnPopoverImports } from '@spartan-ng/brain/popover';
import { BrnPopoverComponent } from '@spartan-ng/brain/popover';
import { BrnCommandImports } from '@spartan-ng/brain/command';
import { HlmIconDirective } from '@spartan-ng/helm/icon';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { HlmCommandImports } from '@spartan-ng/helm/command';
import { SolarInverter } from '@features/catalog/models/inverter.model';

@Component({
  selector: 'app-inverter-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgIconComponent,
    HlmIconDirective,
    ...BrnPopoverImports,
    ...BrnCommandImports,
    ...HlmPopoverImports,
    ...HlmCommandImports,
  ],
  providers: [
    provideIcons({
      lucideSearch,
      lucideZap,
      lucideCheck,
      lucideChevronDown,
      lucidePlus,
      lucideX,
    }),
  ],
  templateUrl: './inverter-selector.html',
  styleUrl: './inverter-selector.css',
})
export class InverterSelector {
  // ViewChild für Popover-Referenz
  @ViewChild(BrnPopoverComponent, { static: true })
  popover!: BrnPopoverComponent;

  // Inputs — data is owned and fetched by the parent smart component
  inverters = input<SolarInverter[]>([]);
  selectedInverterIds = input<string[]>([]);
  placeholder = input<string>('Wechselrichter auswählen...');
  disabled = input<boolean>(false);
  label = input<string>('Wechselrichter');
  minInverters = input<number>(1);
  maxInverters = input<number>(5);
  isLoading = input<boolean>(false);
  error = input<string | null>(null);

  // Outputs
  invertersSelected = output<SolarInverter[]>();
  selectedInverterIdsChange = output<string[]>();

  // Local selection state — independently mutable (user toggles items)
  selectedInvertersLocal = linkedSignal(() => this.selectedInverterIds());

  isInverterSelected(inverterId: string): boolean {
    return this.selectedInvertersLocal().includes(inverterId);
  }

  toggleInverter(inverter: SolarInverter): void {
    const current = this.selectedInvertersLocal();
    const isSelected = current.includes(inverter.id);

    if (isSelected) {
      const updated = current.filter((id) => id !== inverter.id);
      this.selectedInvertersLocal.set(updated);
    } else {
      if (current.length < this.maxInverters()) {
        this.selectedInvertersLocal.set([...current, inverter.id]);
      }
    }

    const selectedIds = this.selectedInvertersLocal();
    const selectedInverters = selectedIds
      .map((id) => this.inverters().find((inv) => inv.id === id))
      .filter((inv): inv is SolarInverter => inv !== undefined);

    this.invertersSelected.emit(selectedInverters);
    this.selectedInverterIdsChange.emit(selectedIds);
  }

  removeInverter(inverterId: string, event: Event): void {
    event.stopPropagation();
    const updated = this.selectedInvertersLocal().filter(
      (id) => id !== inverterId,
    );
    this.selectedInvertersLocal.set(updated);

    const selectedInverters = updated
      .map((id) => this.inverters().find((inv) => inv.id === id))
      .filter((inv): inv is SolarInverter => inv !== undefined);

    this.invertersSelected.emit(selectedInverters);
    this.selectedInverterIdsChange.emit(updated);
  }

  getSelectedInverters(): SolarInverter[] {
    const selectedIds = this.selectedInvertersLocal();
    return selectedIds
      .map((id) => this.inverters().find((inv) => inv.id === id))
      .filter((inv): inv is SolarInverter => inv !== undefined);
  }

  getInverterSearchValue(inverter: SolarInverter): string {
    return `${inverter.name} ${inverter.size}kW ${inverter.cost}€`;
  }

  getInverterDisplay(inverter: SolarInverter): string {
    return `${inverter.name} (${inverter.size}kW • ${inverter.cost}€)`;
  }

  getSelectedInvertersDisplay(): string {
    const selected = this.selectedInvertersLocal();
    if (selected.length === 0) {
      return this.placeholder();
    }
    return `${
      selected.length
    } von ${this.maxInverters()} Wechselrichtern ausgewählt`;
  }

  getInverterById(inverterId: string): SolarInverter | undefined {
    return this.inverters().find((inv) => inv.id === inverterId);
  }
}
